import type React from "react"
import { memo, type ReactNode } from "react"
import {
  Box,
  type BoxProps,
  Card,
  type CardProps,
  Chip,
  styled,
  Typography,
  useTheme,
} from "@mui/material"
import {
  CheckOutlined,
  CloudDownloadRounded,
  ErrorRounded,
  LoopRounded,
  NoEncryptionOutlined,
  ThumbDownOutlined,
} from "@mui/icons-material"
import { Cover } from "./Cover"
import { useBook, useIsBookProtected } from "./states"
import {
  getReadingStateReachedProgress,
  ReadingStateState,
} from "@oboku/shared"
import { useLink } from "../links/states"
import { DownloadState, useBookDownloadState } from "../download/states"
import { ButtonAsIcon } from "../common/ButtonAsIcon"
import { pluginsByType } from "../integrations/plugins/configure"

const CoverIconBadgeRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(0.3),
  borderRadius: "50%",
  backgroundColor: theme.alpha(theme.palette.common.white, 0.7),
  border: `1px solid ${theme.alpha(theme.palette.primary.main, 0.7)}`,
}))

export const CoverIconBadge = memo(function CoverIconBadge({
  children,
  ...rest
}: { children: ReactNode } & BoxProps) {
  return <CoverIconBadgeRoot {...rest}>{children}</CoverIconBadgeRoot>
})

export const BookCoverCard = memo(function BookCoverCard({
  bookId,
  className,
  style,
  withDownloadStatus = true,
  withBadges,
  showBottomBar = false,
  size = "small",
  ...rest
}: {
  bookId: string
  className?: string
  style?: React.CSSProperties
  withDownloadStatus?: boolean
  withBadges: boolean
  showBottomBar?: boolean
  size?: "small" | "large" | "medium"
} & CardProps) {
  const { data: item } = useBook({ id: bookId })
  const { data: link } = useLink({ id: item?.links?.[0] })
  const linkPlugin = link?.type ? pluginsByType[link?.type] : undefined
  const bookDownloadState = useBookDownloadState(bookId)
  const { data: isBookProtected } = useIsBookProtected(item)
  const theme = useTheme()
  const hasBottomBar =
    showBottomBar &&
    (!!linkPlugin ||
      item?.readingStateCurrentState === ReadingStateState.Finished ||
      item?.readingStateCurrentState === ReadingStateState.Reading)

  return (
    <Card
      sx={{
        position: "relative",
        display: "flex",
        width: "100%",
        height: "100%",
        minHeight: 0, // @see https://stackoverflow.com/questions/42130384/why-should-i-specify-height-0-even-if-i-specified-flex-basis-0-in-css3-flexbox
        overflow: "hidden",
      }}
      style={style}
      className={className}
      elevation={0}
      {...rest}
    >
      {item && <Cover bookId={item?._id} />}
      {bookDownloadState?.downloadState !== DownloadState.Downloaded && (
        <Box
          style={{
            opacity: 0.5,
            height:
              bookDownloadState?.downloadState === DownloadState.Downloading
                ? `${100 - (bookDownloadState?.downloadProgress || 0)}%`
                : `100%`,
          }}
          sx={{
            bgcolor: "white",
            top: 0,
            position: "absolute",
            width: "100%",
          }}
        />
      )}
      <Box
        sx={{
          gap: 1,
          position: "absolute",
          height: "100%",
          width: "100%",
          top: 0,
          display: "flex",
          padding: (theme) => theme.spacing(0.5),
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {withBadges && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexDirection: "row",
              width: "100%",
            }}
          >
            <Box
              sx={{
                gap: 1,
                flexDirection: "row",
                display: "flex",
              }}
            >
              {isBookProtected && (
                <CoverIconBadge>
                  <NoEncryptionOutlined color="primary" fontSize={size} />
                </CoverIconBadge>
              )}
              {item?.isNotInterested && (
                <CoverIconBadge>
                  <ThumbDownOutlined color="primary" fontSize={size} />
                </CoverIconBadge>
              )}
            </Box>
          </Box>
        )}
        {withBadges && item?.metadataUpdateStatus === "fetching" && (
          <Chip
            color="primary"
            size="small"
            icon={
              <LoopRounded color="primary" className="oboku-infinite-spin" />
            }
            label="metadata..."
          />
        )}
        {withBadges &&
          item?.metadataUpdateStatus !== "fetching" &&
          !!item?.lastMetadataUpdateError && (
            <Chip
              color="primary"
              size="small"
              icon={<ErrorRounded color="primary" />}
              label="metadata"
            />
          )}

        {withDownloadStatus &&
          bookDownloadState?.downloadState === DownloadState.None && (
            <ButtonAsIcon
              variant="contained"
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <CloudDownloadRounded fontSize={size} />
            </ButtonAsIcon>
          )}
        {withDownloadStatus &&
          bookDownloadState?.downloadState === DownloadState.Downloading && (
            <Box
              sx={{
                transform: "translate(-50%, -50%)",
                position: "absolute",
                left: "50%",
                top: "50%",
              }}
            >
              <Chip color="primary" size="small" label="downloading..." />
            </Box>
          )}
      </Box>
      {hasBottomBar && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            minHeight: theme.spacing(4),
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 0.5,
            boxSizing: "border-box",

            bgcolor:
              theme.palette.mode === "dark"
                ? theme.alpha(theme.palette.common.black, 0.5)
                : theme.alpha(theme.palette.common.white, 0.5),

            lineHeight: 0,
            backdropFilter: "blur(8px)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {linkPlugin && (
              <linkPlugin.Icon fontSize="medium" sx={{ display: "block" }} />
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {item?.readingStateCurrentState === ReadingStateState.Reading && (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                }}
              >
                {Math.floor(
                  (item ? getReadingStateReachedProgress(item) : 0) * 100,
                ) || 1}
                %
              </Typography>
            )}
            {item?.readingStateCurrentState === ReadingStateState.Finished && (
              <CheckOutlined fontSize="medium" />
            )}
          </Box>
        </Box>
      )}
    </Card>
  )
})
